import { Component, inject, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EventDetailsBaseComponent } from 'src/app/modules/event-calendar/components/event.details.modal/event.details.base.component';
import { VisitorEvent } from '../../../models/visitor.event';
import { SharedModule } from '../../../shared.module';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonTriggerEvent } from 'src/app/common/components/button/button.component';
import { VisitorEventService } from 'src/app/modules/event-calendar/services/visitor.event.service';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { RichTextBoxComponent } from 'src/app/common/components/rich.text.box/rich.text.box.component';
import { catchError, finalize, Observable, of, switchMap, takeUntil, tap } from 'rxjs';
import { EditEventCommand } from '../../../models/edit.event.command';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { localStorageKeys } from 'src/app/common/constants/local.storage.keys';
import { TokenInfo } from 'src/app/common/services/auth/auth.service';
import { TabImageLinkFileSelectorComponent } from 'src/app/common/components/tab.image.link.file.selector/tab.image.link.file.selector.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PreviewAiDescriptionComponent } from '../preview.ai.description/preview.ai.description.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { AiDescriptionGeneratorComponent } from '../ai.description.generator/ai.description.generator.component';

@Component({
  selector: 'ig-edit-event-modal',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TabImageLinkFileSelectorComponent,
    AiDescriptionGeneratorComponent,
  ],
  templateUrl: './edit.event.modal.component.html',
  styleUrl: './edit.event.modal.component.scss',
})
export class EditEventModalComponent extends BaseComponent {
  event: VisitorEvent;

  form: FormGroup;
  _fb = inject(FormBuilder);

  editing = {
    basic: false,
  };

  tzNameByFormatter: string;
  isAllowOnlineEvents = true;
  isHideTicketUrl = false;
  eventInfo: string;

  aiGeneratedDescriptionList: string[] = [];
  isGeneratingDescription = false;

  submitBtnTrigger: { trigger: MatMenuTrigger; event: ButtonTriggerEvent } = null;

  eventService = inject(VisitorEventService);
  timeService = inject(EventTimeService);
  errorAlertService = inject(ErrorAlertService);
  modalService = inject(ModalService);
  dashboardService = inject(DashboardService);

  @ViewChild('imgUploader', { static: false })
  imgUploader: TabImageLinkFileSelectorComponent;

  @ViewChild('richTextBox', { static: false })
  richTextBox: RichTextBoxComponent;

  constructor(
    public activatedRoute: ActivatedRoute,
    public dialogRef: MatDialogRef<EventDetailsBaseComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VisitorEvent
  ) {
    super({ activatedRoute });
    this.event = data;
    this.eventInfo = this.getEventInfoForGeneratingDescription();
    if (this.event.owner) {
      this.event.host = this.event.owner.name;
    }

    this.form = this._fb.group({
      name: [null, [Validators.required]],
      description: [null, []],
      category: [null, []],
      timeRanges: this._fb.array([
        this._fb.group({
          startTime: [null, []],
          endTime: [null, []],
        }),
      ]),
      eventTimes: [],
      startTime: [null, []],
      endTime: [null, []],
      address: [null, [Validators.required]],
      host: [null, [Validators.required]],
      venue: [null, []],
      timezone: [null, []],
      hostLink: [null, []],
      eventLink: [null, []],
      organizerEmail: [null, []],
      ticketUri: [null, []],
      imageLink: [null, []],
      isHybridEvent: [false, []],
      isOnlineEvent: [false, []],
      imgFile: [null, []],
      isHideTicketUrl: [false, []],
    });

    this.form.patchValue(this.event);
    if (this.event.address) {
      this.form.get('address').setValue(this.event.address.address);
    }

    this.setEventTimes();
  }

  private setEventTimes() {
    if (this.event?.eventTimes?.length) {
      const timeRangesArray = this.form.get('timeRanges') as FormArray;
      while (timeRangesArray.length > 0) {
        timeRangesArray.removeAt(0);
      }

      this.event.eventTimes.forEach((time) => {
        timeRangesArray.push(
          this._fb.group({
            startTime: [time.startTime, Validators.required],
            endTime: [time.endTime || null],
          })
        );
      });
    }

    this.getTZNameByFormatter();
  }

  edit(section: string) {
    this.editing[section] = true;
  }

  async saveChanges(event?: ButtonTriggerEvent) {
    if (this.imgUploader.waitingUpload()) {
      if (!this.imgUploader.hasCopyrights) {
        this.imgUploader.showCopyRightsAlert();
        event?.immediateComplete();
        return;
      }
      this.imgUploader.submit(event);
      this.submitBtnTrigger = {
        event,
        trigger: null,
      };
      return;
    }

    const address = this.processAddress();
    const isOnline = this.form.get('isOnlineEvent').value;

    if ((typeof address === 'string' || !address) && !isOnline) {
      this.errorAlertService.showErrorAlert(
        'Please type the physical address and select one from the dropdown'
      );
      event?.immediateComplete();
      this.submitBtnTrigger?.event?.immediateComplete();
      return;
    }

    if (this.form.invalid) {
      event?.immediateComplete();
      this.submitBtnTrigger?.event?.immediateComplete();
      return;
    }

    const times = this.form.get('timeRanges')['controls'];
    const eventTimes = [];
    for (const time of times) {
      const start = time.get('startTime').value;
      start &&
        eventTimes.push({
          startTime: start,
          endTime: time.get('endTime').value,
        });
    }
    this.form.get('eventTimes').setValue(eventTimes);

    this.form
      .get('timezone')
      .setValue(
        this.timeService.getTZByFormatter(this.frontendSettings.customSettings.timezoneFormatter)
      );

    let command = <EditEventCommand>this.form.value;
    command.owner = {
      name: command.host,
    };
    if (this.event.cover) {
      command.cover = this.event.cover;
    }
    command.address = address;
    command._id = this.event._id;
    command.title = command.name;

    Object.assign(this.event, {
      ...command,
      ...(this.event.cover && { cover: this.event.cover }),
      address: command.address || this.event.address,
    });

    this.dashboardService
      .editEvent(this.hubName, command)
      .pipe(
        tap(() => {
          event?.immediateComplete();
          this.submitBtnTrigger?.event?.immediateComplete();
          this.close(this.event);
          this.showSuccess('Event updated successfully.');
        }),
        catchError((err) => {
          this.errorAlertService.showErrorAlert(err.message);
          return of(null);
        })
      )
      .subscribe((res) => {
        this.close(res);
      });
  }

  onlineEventChange($event) {
    this.form.get('isOnlineEvent').setValue($event.checked);
    if ($event.checked) {
      this.form.get('address').setValue('Online Event');
      this.form.get('isHybridEvent').setValue(false);
    } else {
      this.form.get('address').setValue(this.event.address?.address || '');
    }
  }

  hybridChange($event) {
    this.form.get('isHybridEvent').setValue($event.checked);
    if ($event.checked) {
      this.form.get('isOnlineEvent').setValue(false);
      const address = this.form.get('address').value;
      if (address === 'Online Event') {
        this.form.get('address').setValue('');
      }
    } else {
      this.form.get('address').setValue(this.event.address?.address || '');
    }
  }

  setAsApprovedChange($event) {
    this.form.get('isApproved').setValue($event.checked);
  }

  close(event?: any) {
    this.dialogRef.close(event);
  }

  onDescriptionChange(content: string) {
    this.form.get('description').setValue(content);
  }

  onAIGeneratedDescriptionSelected(desc: string) {
    if (desc) {
      this.form.get('description').setValue(desc);
      this.richTextBox.setEditorContent(desc);
    }
  }

  getCurrentDescription(): string {
    if (this.richTextBox) {
      return this.richTextBox.getEditorContent();
    }
    return this.form.get('description').value || '';
  }

  onGenerateDescription() {
    // this.aiGeneratedDescriptionList = [
    //   'End your weekend with laughs! The **3rd Sunday Comedy Show** at Fateful Events. Top comedians, non-stop humor—don’t miss it! ️',
    //   'Are You Ready To Dance? Join us at The Y Bar Savannah every Tuesday @6pm for an electrifying dance class presented by Savannah Dance Market',
    //   'Step into a world of adventure—join us for immersive RPG One-Shot Nights at Your Space, where epic tales come to life in intimate settings!',
    // ];
    // const modal = this.modalService.show({
    //   component: PreviewAiDescriptionComponent,
    //   data: this.aiGeneratedDescriptionList,
    //   panelClass: ['!w-[700px]'],
    // });
    // return;
    if (this.isGeneratingDescription) return;
    this.isGeneratingDescription = true;
    this.dashboardService
      .aiAssistant(this.hubName, {
        query: this.eventInfo,
        model: 'chatgpt',
        feature: 'event-description',
      })
      .subscribe((data: { success: boolean; model: string; data: string }) => {
        if (data.success) {
          try {
            const json = JSON.parse(data.data);
            this.aiGeneratedDescriptionList = [json.version1, json.version2, json.version3];
            console.log(this.aiGeneratedDescriptionList);
            this.isGeneratingDescription = false;

            const modal = this.modalService.show({
              component: PreviewAiDescriptionComponent,
              data: this.aiGeneratedDescriptionList,
              panelClass: ['!w-[900px]'],
            });
            modal.afterClosed().subscribe((desc) => {
              if (desc) {
                this.form.get('description').setValue(desc);
                this.richTextBox.setEditorContent(desc);
              }
            });

            // this.trackEventAIContentAssistant();
          } catch (err) {
            this.isGeneratingDescription = false;
            console.error(err);
          }
        }
      });
  }

  onImageUploaded($event: string) {
    if (!this.event.cover) {
      this.event.cover = {
        source: '',
      };
    }
    this.event.cover.source = $event;

    this.saveChanges();
  }

  public get addressInputLabel() {
    if (!this.isAllowOnlineEvents) {
      return `Location / Address *`;
    }
    return `Location / Address ${this.event.isOnlineEvent ? '' : '*'} (or check Virtual Events)`;
  }

  private processAddress() {
    const address = this.form.get('address').value;

    if (!address) {
      return address;
    }

    // doesn't change the address
    if (typeof address === 'string' && this.event.address?.address === address) {
      return this.event.address;
    }

    if (typeof address === 'object') {
      const processedAddress = { ...address };

      if (processedAddress.address_components) {
        processedAddress.addressComponents = processedAddress.address_components;
        delete processedAddress.address_components;
      }

      return processedAddress;
    }

    return address;
  }

  private getTZNameByFormatter() {
    this.tzNameByFormatter = this.timeService.getTZNameByFormatter(
      this.frontendSettings.customSettings.timezoneFormatter
    );
  }

  private getEventInfoForGeneratingDescription() {
    return `event title: ${this.event.name}; address: ${
      this.event.address ? this.event.address.address : ''
    }; venue/host: ${
      this.event.venue || (this.event.venueInfo ? this.event.venueInfo.name : this.event.owner.name)
    }`;
  }
}

export interface IEditEventModalData {
  isForHub: boolean;
}
